import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TraderJargon } from "@/components/TraderJargon";
import ReadingLevelToggle from "@/components/ReadingLevelToggle";

// Machine-readable rule codes (must match /app/backend/services/layer11_rules.py).
function buildRules(level) {
    return [
        { code: "mandatory_hard_stop_loss",        title: <TraderJargon term="mandatory_sl" level={level} />,       subtitle: "Reject any BUY without a stop-loss." },
        { code: "dual_rationale_gate",             title: <TraderJargon term="dual_rationale" level={level} />,     subtitle: "Require two written reasons per trade (Advanced+)." },
        { code: "min_2to1_reward_risk",            title: <TraderJargon term="reward_risk" level={level} />,        subtitle: "Reject if TP < 2 × SL." },
        { code: "no_averaging_down",               title: <TraderJargon term="no_averaging_down" level={level} />,  subtitle: "Block BUYs on symbols where you already hold a losing long." },
        { code: "immutable_stop_loss_floor",       title: <TraderJargon term="immutable_sl" level={level} />,       subtitle: "You can tighten your SL, never loosen it." },
        { code: "daily_3pct_circuit_breaker",      title: <TraderJargon term="circuit_breaker" level={level} />,    subtitle: "Freeze trading for 24h if you lose 3% in a day." },
        { code: "weekly_6pct_drawdown_probation",  title: <TraderJargon term="drawdown_probation" level={level} />, subtitle: "Freeze trading for a week if you lose 6% in 7 days." },
        { code: "post_trade_2h_deescalation",      title: <TraderJargon term="post_trade_lock" level={level} />,    subtitle: "Cool-off after a >5% profit or loss." },
    ];
}

const KILL_SWITCH_STRINGS = {
    en: "I ACCEPT RESPONSIBILITY FOR MY BIASES",
    sw: "NINAKUBALI HASARA ZA MIHEMKO YANGU",
    sh: "NIMEKUBALI BEBA LOSS ZA AKILI YANGU",
    fr: "J'ACCEPT LA RESPONSABILITÉ DE MES BIAIS",
    ar: "أنا أتحمل المسؤولية عن تحيزاتي",
    pt: "ACEITO A RESPONSABILIDADE PELOS MEUS VIESES",
};

/**
 * Layer11SettingsCard — per-rule opt-out toggles + kill-switch modal.
 */
export default function Layer11SettingsCard() {
    const { user, refresh } = useAuth() || {};
    const [optOuts, setOptOuts] = useState(user?.layer11_opt_outs || []);
    const [killTyped, setKillTyped] = useState("");
    const [readingLevel, setReadingLevel] = useState(user?.reading_level || "basic");
    const lang = user?.preferred_language || "en";
    const killString = KILL_SWITCH_STRINGS[lang] || KILL_SWITCH_STRINGS.en;

    useEffect(() => { setOptOuts(user?.layer11_opt_outs || []); }, [user]);
    useEffect(() => { setReadingLevel(user?.reading_level || "basic"); }, [user]);
    const RULES = buildRules(readingLevel);

    async function toggle(rule_code, opt_out) {
        try {
            const { data } = await api.post("/prefs/layer11-opt-out", { rule_code, opt_out });
            setOptOuts(data.layer11_opt_outs || []);
            toast.success(opt_out ? `Opted out of ${rule_code}` : `Re-enabled ${rule_code}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Toggle failed");
        }
    }

    async function killSwitch() {
        try {
            await api.post("/prefs/layer11-kill-switch",
                            { typed_confirmation: killTyped, language: lang });
            toast.success("All Layer 11 rules disabled. Compliance flag raised.");
            if (typeof refresh === "function") await refresh();
            setKillTyped("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kill-switch failed");
        }
    }

    return (
        <TooltipProvider delayDuration={150}>
        <div className="border border-border bg-card rounded-xl p-4" data-testid="layer11-settings-card">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                        <Shield className="w-4 h-4" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                            Behavioural guardrails
                        </div>
                        <div className="text-sm font-medium">Layer 11 · Programmatic Rules</div>
                    </div>
                </div>
                <ReadingLevelToggle value={readingLevel} onChange={setReadingLevel} />
            </div>
            <ul className="divide-y divide-border">
                {RULES.map((r) => {
                    const off = optOuts.includes(r.code);
                    return (
                        <li key={r.code} className="flex items-center justify-between gap-3 py-2.5">
                            <div className="min-w-0">
                                <div className="text-sm font-medium">{r.title}</div>
                                <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>
                            </div>
                            <Switch
                                checked={!off}
                                onCheckedChange={(v) => toggle(r.code, !v)}
                                data-testid={`layer11-toggle-${r.code}`}
                            />
                        </li>
                    );
                })}
            </ul>

            <div className="mt-4 pt-4 border-t border-border">
                <Dialog>
                    <DialogTrigger asChild>
                        <Button variant="destructive" size="sm" data-testid="layer11-kill-switch-open">
                            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Disable ALL guardrails
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md" data-testid="layer11-kill-switch-modal">
                        <DialogHeader>
                            <DialogTitle className="text-rose-500 flex flex-col gap-0.5">
                                <span>Critical: disable capital protection</span>
                                <span className="text-xs font-normal text-muted-foreground">
                                    (<TraderJargon term="kill_switch" level={readingLevel} side="bottom" />)
                                </span>
                            </DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-muted-foreground">
                            You're about to strip your training environment of every
                            programmatic guardrail. 87% of retail traders lose money
                            to psychological biases these rules mitigate. Your opt-out
                            will be flagged on your institution's compliance dashboard.
                        </p>
                        <p className="text-xs font-medium">
                            Type this string exactly to confirm:
                        </p>
                        <div className="mono text-xs bg-muted/40 border border-border rounded p-2 text-center">
                            {killString}
                        </div>
                        <Input
                            value={killTyped}
                            onChange={(e) => setKillTyped(e.target.value)}
                            placeholder="Type the phrase above"
                            data-testid="layer11-kill-switch-input"
                        />
                        <Button
                            variant="destructive"
                            disabled={killTyped.trim() !== killString}
                            onClick={killSwitch}
                            data-testid="layer11-kill-switch-confirm"
                        >
                            Disable all guardrails
                        </Button>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
        </TooltipProvider>
    );
}
